import { ApiClient, ApiError } from '@schemas'
import { baseUrl, fetchWithActorHeaders } from '../lib/apiClient'

// Two seconds leaves room for the request/render within the five-second LAN
// observation target. Only shared show read models opt in to this policy.
export const LIVE_QUERY_INTERVAL_MS = 2_000
type LiveQuery = { state: { error: Error | null; errorUpdatedAt: number } }
// A refused read is refused again on the next poll, and a query without data shows its loading
// state on every attempt. A 401 sends the browser to sign in; a 403 is retried when the
// signed-in user's roles change (retryRefusedReadsOnRoleChange).
const isRefused = (error: Error | null) =>
  error instanceof ApiError && (error.status === 401 || error.status === 403)
// Milliseconds left of a 429's Retry-After; zero or less once the read may be retried.
function rateLimitWait({ error, errorUpdatedAt }: LiveQuery['state']) {
  if (!(error instanceof ApiError && error.status === 429)) return 0
  return errorUpdatedAt + (error.retryAfterMs ?? 60_000) - Date.now()
}
export function liveRefetchInterval(query: LiveQuery) {
  if (isRefused(query.state.error)) return false
  return Math.max(LIVE_QUERY_INTERVAL_MS, rateLimitWait(query.state))
}
// A shown tab catches up at once, except on a read that is refused or still waiting out a 429.
export function liveRefetchOnFocus(query: LiveQuery) {
  return isRefused(query.state.error) || rateLimitWait(query.state) > 0
    ? false
    : ('always' as const)
}
export const liveQueryOptions = {
  refetchInterval: liveRefetchInterval,
  // A hidden tab (switched away, minimized or locked) does not poll; a window on screen does,
  // including one that is not focused, such as a board on a second display.
  refetchIntervalInBackground: false,
  refetchOnWindowFocus: liveRefetchOnFocus,
  refetchOnReconnect: 'always' as const,
  // navigator.onLine describes the browser's network, not whether the local
  // server is reachable. A failed read gets one attempt per interval, without
  // an additional retry loop; recovery must not wait for a long backoff timer.
  networkMode: 'always' as const,
  retry: false,
}

// Bound both response headers and body consumption. A black-holed connection
// must release the query so the next interval can observe a recovered LAN.
export const LIVE_READ_TIMEOUT_MS = 2_000
export const liveApiClient = new ApiClient({
  baseUrl,
  fetchImpl: (input, init) => {
    const timeout = AbortSignal.timeout(LIVE_READ_TIMEOUT_MS)
    const signal = init?.signal ? AbortSignal.any([init.signal, timeout]) : timeout
    return fetchWithActorHeaders(input, { ...init, signal })
  },
})
